import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSupabase } from '../../supabaseAuth';
import { fetchMetalPrices } from '../../store/slices/metalPriceSlice';
import { GoldIcon, SilverIcon, PlatinumIcon } from '../icons/MetalIcons';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { logout, isAuthenticated, user } = useSupabase();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Get metal prices from Redux
  const { prices: metalPrices = {} } = useSelector((state) => state.metalPrices);

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  // Fetch metal prices (for both authenticated and non-authenticated users)
  useEffect(() => {
    dispatch(fetchMetalPrices());
    // Refresh prices every 60 seconds
    const interval = setInterval(() => {
      dispatch(fetchMetalPrices());
    }, 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Compact metal price display component
  const MetalPriceDisplay = ({ metal, data }) => {
    if (!data || !data.price_per_ounce) return null;

    const price = data.price_per_ounce;
    const previous = data.previous;

    // Calculate price change percentage
    let priceChange = 0;
    if (previous && !isNaN(previous) && previous > 0) {
      priceChange = ((price - previous) / previous) * 100;
    }

    const isPositive = priceChange >= 0;

    const getMetalIcon = () => {
      switch(metal) {
        case 'gold': return <GoldIcon size={20} />;
        case 'silver': return <SilverIcon size={20} />;
        case 'platinum': return <PlatinumIcon size={20} />;
        default: return null;
      }
    };

    const metalColors = {
      gold: 'text-yellow-600',
      silver: 'text-gray-500',
      platinum: 'text-gray-400'
    };

    // Format price with commas
    const formatPrice = (num) => {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
        <div className="w-5 h-5 flex-shrink-0">
          {getMetalIcon()}
        </div>
        <span className={`text-sm font-bold uppercase ${metalColors[metal]} whitespace-nowrap`}>
          {metal}
        </span>
        <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
          ${formatPrice(price)}/oz
        </span>
        {priceChange !== 0 && (
          <span className={`text-sm font-medium whitespace-nowrap ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '▲' : '▼'} {Math.abs(priceChange).toFixed(1)}%
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 w-full z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center w-full px-1 py-3 sm:px-4 md:px-6">
            {/* Logo - left aligned */}
            <div
              className="flex-shrink-0 flex items-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img
                src="/logo.png"
                alt="Auric Logo"
                className="h-8"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {/* Spacer - pushes content to edges */}
            <div className="flex-1" />

            {/* Metal Prices - Desktop only, centered */}
            <div className="hidden md:flex items-center justify-center gap-2">
              <MetalPriceDisplay metal="gold" data={metalPrices.gold} />
              <MetalPriceDisplay metal="silver" data={metalPrices.silver} />
              <MetalPriceDisplay metal="platinum" data={metalPrices.platinum} />
            </div>

            {/* Spacer - pushes content to edges */}
            <div className="hidden md:flex flex-1" />

            {/* Right Side Icons/Actions */}
            <div className="flex-shrink-0 flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  {/* Notification Bell */}
                  <button
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Notifications"
                  >
                    <svg
                      className="w-5 h-5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                  </button>

                  {/* Profile Dropdown */}
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white text-sm font-medium">
                        {user?.user_metadata?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <svg
                        className={`w-4 h-4 text-gray-600 hidden md:block transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {showDropdown && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200">
                        <div className="py-1">
                          <button
                            onClick={() => {
                              setShowDropdown(false);
                              navigate('/account');
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            My Account
                          </button>
                          <button
                            onClick={() => {
                              setShowDropdown(false);
                              navigate('/contact');
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Contact Us
                          </button>
                          <button
                            onClick={() => {
                              setShowDropdown(false);
                              handleSignOut();
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-t border-gray-100"
                          >
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/login')}
                    className="text-sm font-medium text-gray-700 hover:text-black transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/signup')}
                    className="bg-black text-white px-4 py-1.5 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              )}

              {/* Mobile Menu Button */}
              {isAuthenticated && (
                <button
                  className="md:hidden p-2 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Menu"
                >
                  <svg
                    className="w-6 h-6 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
              )}
            </div>
        </div>
      </nav>

      {/* Main Content with top padding for fixed navbar */}
      <div className="w-full">
        <Outlet />
      </div>
    </div>
  );
};

export default Layout;
